import * as Yup from 'yup';
import {
  isBefore,
  differenceInDays,
  differenceInHours,
  subHours,
  addHours,
  differenceInMinutes,
  startOfDay,
} from 'date-fns';
import { freightTypeEnum } from '../../../../hooks/freights/useConfigureFreight';

const schema = Yup.object()
  .shape({
    isInternalFreight: Yup.boolean().default(false),
    freightType: Yup.string()
      .required('Tipo de frete é obrigatório')
      .typeError('Por favor selecione um tipo de frete'),
    driverId: Yup.string()
      .when('freightType', {
        is: freightTypeEnum.PRIVATE,
        then: Yup.string().required('Motorista é obrigatório'),
      })
      .typeError('Por favor selecione um motorista'),
    vehicleId: Yup.string()
      .when(
        ['freightType', 'isInternalFreight'],
        (freightType: string, isInternalFreight: boolean, field: any) => {
          if (freightType === freightTypeEnum.PRIVATE && isInternalFreight) {
            return field.required('Veículo é obrigatório');
          }
          return field.notRequired();
        },
      )
      .typeError('Por favor selecione um veículo'),
    numberOfHelpers: Yup.number()
      .typeError('Número de ajudantes deve ser um valor numérico válido')
      .required('Número de ajudantes é obrigatório')
      .test(
        'is-valid-number',
        'Número de ajudantes deve ser um valor numérico ',
        function (value) {
          return typeof value === 'number' && !isNaN(value);
        },
      )
      .test(
        'is-postive-number',
        'Número de ajudantes deve ser um valor numérico positivo ',
        function (value) {
          return typeof value === 'number' && !isNaN(value) && value >= 0;
        },
      ),
    vehicleTypeId: Yup.string().when('$disableVehicleType', {
      is: true,
      then: (schema: any) => schema.notRequired(),
      otherwise: (schema: any) =>
        schema.required('Tipo de veículo é obrigatório'),
    }),
    pickUpDate: Yup.date()
      .required('Data de retirada é obrigatória')
      .typeError('Por favor escolha uma data de retirada')
      .test(
        'is-before-deadline',
        'Data de retirada não pode ser posterior à data limite de entrega',
        function (value) {
          const { deliveryLimitDate } = this.parent;

          if (!value || !deliveryLimitDate) {
            return false;
          }

          return (
            differenceInDays(new Date(deliveryLimitDate), new Date(value)) >= 0
          );
        },
      )
      .test(
        'is-before-deadline',
        'Data de retirada não pode ser posterior a hoje',
        function (value) {
          if (!value) {
            return false;
          }

          const now = new Date(Date.now());
          return (
            differenceInDays(new Date(value), startOfDay(new Date(now))) >= 0
          );
        },
      ),
    deliveryLimitDate: Yup.date()
      .required('Data limite de entrega é obrigatória')
      .typeError('Por favor escolha uma data de limite de entrega')
      .test(
        'is-after-limitDate',
        'Data de limite de entrega não pode ser anterior à data de retirada',
        function (value) {
          const { pickUpDate } = this.parent;

          if (!value || !pickUpDate) {
            return false;
          }

          return differenceInDays(new Date(value), new Date(pickUpDate)) >= 0;
        },
      ),
    pickUpTime: Yup.string()
      .required('Horário de retirada é obrigatório')
      .typeError('Por favor insira um horário válido')
      .test(
        'is-before-now-pickUpTime',
        'Horário inserido não pode ser agora',
        function (value) {
          const { pickUpDate } = this.parent as { pickUpDate?: string | Date };

          if (!value || !pickUpDate) {
            return false;
          }

          const [hours, minutes] = String(value).split(':').map(Number);

          let year: number | undefined;
          let month: number | undefined;
          let day: number | undefined;

          if (typeof pickUpDate === 'string') {
            const parts = pickUpDate.split('-').map(Number);
            year = parts[0];
            month = parts[1];
            day = parts[2];
          } else if (pickUpDate instanceof Date) {
            year = pickUpDate.getFullYear();
            month = pickUpDate.getMonth() + 1;
            day = pickUpDate.getDate();
          } else {
            return false;
          }

          const pickupDateTime = new Date(
            year || new Date().getFullYear(),
            (month ? month - 1 : new Date().getMonth()),
            day || new Date().getDate(),
            hours || 0,
            minutes || 0,
            0,
            0,
          );

          const now = new Date();

          return differenceInMinutes(pickupDateTime, now) >= 0;
        },
      ),
  })
  .required();

export type formSchemaType = Yup.InferType<typeof schema>;

export default schema;
